import React, { useState, useEffect, useRef } from 'react';
import { testService } from '../../../services/testService';
import { isAssessmentFlagOn } from '../../../utils/assessmentStudent';
import toast from 'react-hot-toast';
import { 
  FiShield, 
  FiLock, 
  FiEye, 
  FiSkipBack,
  FiMaximize,
  FiSave,
  FiAlertTriangle
} from 'react-icons/fi';

const staticRoles = [
  { id: 1, name: 'Developers' }, 
  { id: 2, name: 'Designers' }, 
  { id: 3, name: 'Testers' }, 
  { id: 4, name: 'HR' }, 
  { id: 5, name: 'Client Advocate' }
];

const generateRandomCode = () => `TST-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const TEST_CATEGORY_OPTIONS = [
  { value: 'Enova', label: 'Internal' },
  { value: 'HR Recruitment-Fresher', label: 'External' },
];

const TEST_TYPE_OPTIONS = ['Technical', 'Compliance', 'Behavioral'];

const getDefaultFormState = () => ({
  ass_name: '',
  ass_category: 'Enova',
  ass_type: '',
  ass_assigned: [],
  ass_add_video: 'No',
  ass_desc: '',
  ass_code: generateRandomCode(),
  proctored_exam: false,
  browser_lockdown: false,
  show_results: false,
  allow_backtracking: false,
});

const CreateTestPanel = ({ isOpen, onClose, editingTest, onSuccess }) => {
  const [formData, setFormData] = useState(getDefaultFormState());
  const [isRolesOpen, setIsRolesOpen] = useState(false);
  const [selectedVideoFiles, setSelectedVideoFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [dynamicRoles, setDynamicRoles] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [rolesSearchTerm, setRolesSearchTerm] = useState('');
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const rolesDropdownRef = useRef(null);
  const testNameInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && !editingTest) {
      setTimeout(() => {
        if (testNameInputRef.current) {
          testNameInputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen, editingTest]);

  const activeRoles = dynamicRoles.length > 0 ? dynamicRoles : staticRoles;

  const selectedRoleLabels = formData.ass_assigned
    .map((id) => {
      const found = activeRoles.find((r) => String(r.id) === String(id));
      return found ? found.name : id;
    })
    .join(', ');

  const visibleRoles = activeRoles.filter((role) =>
    role.name.toLowerCase().includes(rolesSearchTerm.toLowerCase())
  );

  // Reset form when modal opens/closes or when editingTest changes
  useEffect(() => {
    if (isOpen) {
      // Roles moved to Groups panel — legacy fetch kept for future use.
      // const fetchRoles = async () => {
      //   try {
      //     const fetchedRoles = await testService.getJobRoles();
      //     const rolesList = fetchedRoles?.data ?? (Array.isArray(fetchedRoles) ? fetchedRoles : []);
      //     if (Array.isArray(rolesList) && rolesList.length > 0) {
      //       setDynamicRoles(rolesList);
      //     }
      //   } catch (error) {
      //     console.error('Error fetching job roles:', error);
      //   }
      // };
      // fetchRoles();

      if (editingTest) {
        // Edit mode: populate existing test data
        setFormData({
          ass_name: editingTest.name || '',
          ass_category: editingTest.category || 'Enova',
          ass_type: editingTest.assessment_type || editingTest.type || '',
          // ass_assigned: editingTest.assigned_to ? editingTest.assigned_to.split(',').filter(Boolean) : [],
          ass_assigned: [],
          ass_add_video: isAssessmentFlagOn(editingTest.add_video) ? 'Yes' : 'No',
          ass_desc: editingTest.instructions || editingTest.description || '',
          ass_code: editingTest.code || generateRandomCode(),
          proctored_exam: isAssessmentFlagOn(editingTest.proctored_exam),
          browser_lockdown: isAssessmentFlagOn(editingTest.browser_lockdown),
          show_results: isAssessmentFlagOn(editingTest.show_results),
          allow_backtracking: isAssessmentFlagOn(editingTest.allow_backtracking),
        });
        const iv = editingTest.intro_videos;
        setExistingVideos(iv ? (typeof iv === 'string' ? JSON.parse(iv) : iv) : []);
      } else {
        // Create mode: reset to defaults
        setFormData(getDefaultFormState());
        setExistingVideos([]);
      }
      setSelectedVideoFiles([]);
      setIsRolesOpen(false);
      setValidationErrors({});
      setRolesSearchTerm('');
    }
  }, [isOpen, editingTest]);

  // Handle click outside for roles dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rolesDropdownRef.current && !rolesDropdownRef.current.contains(event.target)) {
        setIsRolesOpen(false);
      }
    };

    if (isRolesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    if (!isRolesOpen) {
      setRolesSearchTerm('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isRolesOpen]);

  const getFileIcon = (filename) => {
    if (!filename) return 'bi-file-earmark';
    const ext = filename.split('.').pop().toLowerCase();
    if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) return 'bi-play-fill';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'bi-image';
    if (ext === 'pdf') return 'bi-file-pdf';
    if (['doc', 'docx'].includes(ext)) return 'bi-file-earmark-word';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'bi-file-earmark-excel';
    if (['ppt', 'pptx'].includes(ext)) return 'bi-file-earmark-ppt';
    if (ext === 'txt') return 'bi-file-earmark-text';
    return 'bi-file-earmark';
  };

  const showVideoUpload = formData.ass_add_video === 'Yes';
  const isEditMode = !!editingTest;

  const updateField = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
  };

  const toggleRole = (role) => {
    setFormData((prev) => {
      const assigned = prev.ass_assigned.includes(role)
        ? prev.ass_assigned.filter((item) => item !== role)
        : [...prev.ass_assigned, role];
      return { ...prev, ass_assigned: assigned };
    });
  };

  const selectAllRoles = (checked) => {
    const visibleIds = visibleRoles.map((r) => String(r.id));
    
    setFormData((prev) => {
      const currentAssigned = prev.ass_assigned.map(String);
      let nextAssigned;
      if (checked) {
        nextAssigned = [...new Set([...currentAssigned, ...visibleIds])];
      } else {
        nextAssigned = currentAssigned.filter(id => !visibleIds.includes(id));
      }
      return { ...prev, ass_assigned: nextAssigned };
    });
  };

  const handleCategoryChange = (category) => {
    updateField({
      ass_category: category,
      ass_assigned: category === 'Enova' ? formData.ass_assigned : [],
    });
  };

  const handleVideoFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const videoOnly = files.filter((file) => file.type.startsWith('video/'));
    const rejected = files.filter((file) => !file.type.startsWith('video/'));
    if (rejected.length > 0) {
      toast.error(`Only video files are allowed: ${rejected.map((f) => f.name).join(', ')}`);
    }
    if (!videoOnly.length) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Check file size (4MB limit)
    const maxSize = 4 * 1024 * 1024; // 4MB
    const oversizedFiles = videoOnly.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      toast.error(`Some videos exceed 4MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const nextFiles = [...selectedVideoFiles, ...videoOnly].slice(0, 5);
    setSelectedVideoFiles(nextFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeVideoFile = (index) => {
    setSelectedVideoFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.ass_name.trim()) {
      errors.ass_name = 'Test name is required';
    }
    if (!formData.ass_category) {
      errors.ass_category = 'Test category is required';
    }
    if (!formData.ass_type) {
      errors.ass_type = 'Test type is required';
    }
    // Roles are assigned per group (Groups panel), not on Create New Test.
    // if (isEnova && formData.ass_assigned.length === 0) {
    //   errors.ass_assigned = 'Assign at least one role';
    // }
    if (!formData.ass_desc.trim()) {
      errors.ass_desc = 'Test instruction is required';
    }
    if (showVideoUpload && selectedVideoFiles.length === 0 && existingVideos.length === 0) {
      errors.ass_add_video = 'Upload at least one video or set Video to No';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    name: formData.ass_name.trim(),
    category: formData.ass_category,
    code: formData.ass_code || generateRandomCode(),
    assessment_type: formData.ass_type || null,
    // assigned_to: isEnova ? formData.ass_assigned.join(',') : null,
    assigned_to: null,
    description: formData.ass_desc.trim().substring(0, 500),
    instructions: formData.ass_desc.trim(),
    proctored_exam: formData.proctored_exam ? 1 : 0,
    browser_lockdown: formData.browser_lockdown ? 1 : 0,
    show_results: formData.show_results ? 1 : 0,
    allow_backtracking: formData.allow_backtracking ? 1 : 0,
    add_video: showVideoUpload ? 1 : 0,
  });

  const uploadIntroVideos = async (testId) => {
    if (!selectedVideoFiles.length) return;
    try {
      const response = await testService.uploadIntroVideos(testId, selectedVideoFiles);
      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to upload videos');
      }
    } catch (error) {
      console.error('Video upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!validateForm()) return;

    setSubmitting(true);
    const payload = buildPayload();

    try {
      if (isEditMode) {
        const response = await testService.updateTest(editingTest.id, payload);
        if (response.status !== 'success') {
          throw new Error(response.message || 'Failed to update test');
        }
        if (showVideoUpload && selectedVideoFiles.length > 0) {
          await uploadIntroVideos(editingTest.id);
        }
        toast.success('Test updated successfully');
      } else {
        const response = await testService.createTest(payload);
        if (response.status !== 'success') {
          throw new Error(response.message || 'Failed to create test');
        }
        if (showVideoUpload && selectedVideoFiles.length > 0) {
          await uploadIntroVideos(response.id);
        }
        toast.success('Test created successfully');
      }
      onSuccess?.();
      handleClose(); // Close the modal upon success
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Unable to save test');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(getDefaultFormState());
    setSelectedVideoFiles([]);
    setIsRolesOpen(false);
    setValidationErrors({});
    if (onClose) onClose();
  };

  return (
    <div id="TestModal" className={`test-form-inline-panel ${isOpen ? 'open' : ''}`} role="region" aria-label="Create or edit test">
      <div className="test-form-slide-inner">
        <div className="test-create-sheet">
          <div className="flex justify-between items-start gap-4 mb-8">
            <div>
              <h3 className="text-xl font-extrabold text-slate-800 tracking-tight">
                {isEditMode ? 'Edit Test' : 'Create New Test'}
              </h3>
              {isEditMode && (
                <p id="test_form_subtitle" className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1.5 mb-0">
                  Update your assessment details
                </p>
              )}
            </div>
            <button
              type="button"
              className="text-slate-400 hover:text-red-500 transition-colors shrink-0 p-1"
              onClick={handleClose}
              aria-label="Close"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>

          <form ref={formRef} onSubmit={handleSubmit}>
            <input type="hidden" id="ass_code" value={formData.ass_code} />

            <div id="test_form_row1" className="assess-mod-create-test-row grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 mb-6 items-start">
              <div className="assess-mod-create-test-field form-group lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                  Test Name <span className="text-red-500">*</span>
                </label>
                <input
                  ref={testNameInputRef}
                  id="ass_name"
                  value={formData.ass_name}
                  onChange={(e) => updateField({ ass_name: e.target.value })}
                  className={`input h-11 w-full bg-slate-50 border-slate-100 rounded-xl px-3 text-sm focus:border-red-500 focus:bg-white outline-none focus:ring-0 ${
                    validationErrors.ass_name ? 'border-red-500' : ''
                  }`}
                  placeholder="e.g., Technical Proficiency Test"
                />
                {validationErrors.ass_name && (
                  <span className="error-msg block text-red-500 text-[10px] mt-1">{validationErrors.ass_name}</span>
                )}
              </div>

              <div className="assess-mod-create-test-field form-group lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                  Test Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="ass_category"
                  value={formData.ass_category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className={`select h-11 w-full bg-slate-50 border-slate-100 rounded-xl px-3 text-sm focus:border-red-500 focus:bg-white outline-none focus:ring-0 ${
                    validationErrors.ass_category ? 'border-red-500' : ''
                  }`}
                >
                  {TEST_CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {validationErrors.ass_category && (
                  <span className="error-msg block text-red-500 text-[10px] mt-1">{validationErrors.ass_category}</span>
                )}
              </div>

              <div className="assess-mod-create-test-field form-group lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                  Test Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="ass_type"
                  value={formData.ass_type}
                  onChange={(e) => updateField({ ass_type: e.target.value })}
                  className={`select h-11 w-full bg-slate-50 border-slate-100 rounded-xl px-3 text-sm focus:border-red-500 focus:bg-white outline-none focus:ring-0 ${
                    validationErrors.ass_type ? 'border-red-500' : ''
                  }`}
                >
                  <option value="">Select type...</option>
                  {TEST_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {validationErrors.ass_type && (
                  <span className="error-msg block text-red-500 text-[10px] mt-1">{validationErrors.ass_type}</span>
                )}
              </div>

              <div id="ass_add_video_group" className="assess-mod-create-test-field form-group lg:col-span-2">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                  Does it Include the Video? <span className="text-red-500">*</span>
                </label>
                <select
                  id="ass_add_video"
                  value={formData.ass_add_video}
                  onChange={(e) => updateField({ ass_add_video: e.target.value })}
                  className="select h-11 w-full bg-slate-50 border-slate-100 rounded-xl px-3 text-sm focus:border-red-500 focus:bg-white outline-none focus:ring-0"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              <div
                className={`assess-mod-create-test-field assess-mod-create-test-upload lg:col-span-4 ${
                  showVideoUpload ? '' : 'invisible pointer-events-none'
                }`}
                aria-hidden={!showVideoUpload}
              >
                <div className="flex flex-col h-full">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Upload Video <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col gap-3">
                    <div 
                      className={`flex items-center justify-between h-11 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl px-4 cursor-pointer hover:border-red-500 hover:bg-red-50/30 transition-all group ${validationErrors.ass_add_video ? 'border-red-500' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-white rounded-lg flex items-center justify-center text-red-500 shadow-sm group-hover:scale-110 transition-transform">
                          <i className="bi bi-cloud-arrow-up-fill"></i>
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          {selectedVideoFiles.length + existingVideos.length > 0 
                            ? `${selectedVideoFiles.length + existingVideos.length} video(s) selected` 
                            : 'Upload video (4MB and below)'}
                        </span>
                      </div>
                      <span className="text-[9px] font-black text-red-500 uppercase tracking-wider bg-white px-2 py-1 rounded-lg border border-slate-100 shadow-sm">Browse</span>
                      <input
                        type="file"
                        ref={fileInputRef}
                        id="ass_intro_video_input"
                        accept="video/*"
                        multiple
                        className="hidden"
                        onChange={handleVideoFilesChange}
                      />
                    </div>

                    {/* Improved File List UI */}
                    {(selectedVideoFiles.length > 0 || existingVideos.length > 0) && (
                      <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        {existingVideos.map((url, index) => (
                          <div key={`existing-${index}`} className="group relative flex items-center gap-2 p-2 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-emerald-200 transition-all">
                            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                              <i className={`bi ${getFileIcon(url)} text-sm`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[9px] font-black text-slate-700 truncate">{url.split('/').pop()}</p>
                              <p className="text-[7px] font-black text-emerald-500 uppercase tracking-widest">Already Uploaded</p>
                            </div>
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="w-6 h-6 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all shadow-sm"
                            >
                              <i className="bi bi-eye-fill text-[10px]"></i>
                            </a>
                          </div>
                        ))}
                        
                        {selectedVideoFiles.map((file, index) => (
                          <div key={`new-${index}`} className="group relative flex items-center gap-2 p-2 bg-white border border-slate-100 rounded-xl shadow-sm hover:border-red-200 transition-all">
                            <div className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                              <i className={`bi ${getFileIcon(file.name)} text-sm`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[9px] font-black text-slate-700 truncate">{file.name}</p>
                              <p className="text-[7px] font-black text-slate-400 uppercase tracking-widest">New Upload</p>
                            </div>
                            <button
                              type="button"
                              className="w-6 h-6 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow-sm"
                              onClick={() => removeVideoFile(index)}
                            >
                              <i className="bi bi-trash3-fill text-[10px]"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {validationErrors.ass_add_video && (
                    <span className="error-msg block text-red-500 text-[10px] mt-1">{validationErrors.ass_add_video}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8 items-stretch">
              <div className="flex flex-col gap-1.5 min-h-0 h-full">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0">
                  Instruction to the Exam Taker <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="ass_desc"
                  value={formData.ass_desc}
                  onChange={(e) => updateField({ ass_desc: e.target.value })}
                  className={`input bg-slate-50 border-slate-100 rounded-xl p-4 text-sm w-full flex-1 min-h-[12rem] lg:min-h-0 resize-none focus:border-red-500 focus:bg-white outline-none focus:ring-0 transition-all ${
                    validationErrors.ass_desc ? 'border-red-500' : ''
                  }`}
                  rows="7"
                  placeholder="Briefly provide instructions..."
                />
                {validationErrors.ass_desc && (
                  <span className="error-msg block text-red-500 text-[10px] mt-1">{validationErrors.ass_desc}</span>
                )}
              </div>

              <div className="flex flex-col gap-1.5 min-h-0 h-full">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0">
                  Exam configurations
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1 auto-rows-fr min-h-[12rem] lg:min-h-0">
                  <ConfigCard
                    label="Allow users to move between questions"
                    desc=""
                    value={formData.allow_backtracking ? 'Yes' : 'No'}
                    onChange={(v) => updateField({ allow_backtracking: v === 'Yes' })}
                    options={['Yes', 'No']}
                    Icon={FiSkipBack}
                  />
                  <ConfigCard
                    label="Submit automatically when the time ends"
                    desc=""
                    value={formData.show_results ? 'Yes' : 'No'}
                    onChange={(v) => updateField({ show_results: v === 'Yes' })}
                    options={['Yes', 'No']}
                    Icon={FiSave}
                  />
                  <ConfigCard
                    label="Keep the exam in the full screen mode"
                    desc=""
                    value={formData.proctored_exam ? 'Yes' : 'No'}
                    onChange={(v) => updateField({ proctored_exam: v === 'Yes' })}
                    options={['Yes', 'No']}
                    Icon={FiMaximize}
                  />
                  <ConfigCard
                    label="Show warning when leaving exam tab"
                    desc=""
                    value={formData.browser_lockdown ? 'Yes' : 'No'}
                    onChange={(v) => updateField({ browser_lockdown: v === 'Yes' })}
                    options={['Yes', 'No']}
                    Icon={FiAlertTriangle}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t border-slate-100">
              <button
                type="button"
                className="btn-assessment btn-assessment-secondary w-full sm:w-auto"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-assessment btn-assessment-danger w-full sm:w-auto"
              >
                {submitting ? 'Saving...' : isEditMode ? 'Update Test' : 'Create Test'}
                {!submitting && <i className={`bi bi-${isEditMode ? 'pencil-square' : 'rocket-takeoff'}`}></i>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ConfigCard = ({ label, desc, value, onChange, options, Icon }) => {
  const isChecked = value === options[0];
  
  return (
    <div 
      className={`transition-all border rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md ${isChecked ? 'bg-[#f8fafc] border-slate-200' : 'bg-[#fcfdfe] border-slate-100 hover:bg-white'}`}
      onClick={() => onChange(isChecked ? options[1] : options[0])}
    >
      <div className="w-12 h-12 bg-white border border-slate-100 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
        {Icon && <Icon className="text-xl text-red-500" />}
      </div>
      <div className="flex-1">
        <div className="text-xs font-black text-slate-800 leading-tight">{label}</div>
        {desc && <p className="text-[10px] text-slate-500 font-bold mb-0 mt-0.5 leading-snug">{desc}</p>}
      </div>
      <div className="shrink-0">
        <div 
          className={`w-10 h-6 rounded-full p-1 transition-all duration-300 relative ${isChecked ? 'bg-red-600' : 'bg-slate-200'}`}
        >
          <div 
            className={`w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-300 transform ${isChecked ? 'translate-x-4' : 'translate-x-0'}`}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateTestPanel;
